import { SubscriptionManager } from '@/classes/SubscriptionManager';
import { EPosition } from 'common/enum/misc.enum';

export type TPositionablePopoverDrawerCloseReason = 'backdrop' | 'button' | 'cancel';

export const canClosePositionablePopoverDrawer = (
  reason: TPositionablePopoverDrawerCloseReason,
  disableClickOutside: boolean,
): boolean => reason !== 'backdrop' || !disableClickOutside;

/** Heights already exclude viewport margins; chrome is the space outside the content wrapper. */
export const getPopoverContentHeightBudget = ({
  viewportHeight,
  availableSpaceFromAnchor,
  chromeHeight,
  minContentHeight,
}: {
  viewportHeight: number;
  availableSpaceFromAnchor: number;
  chromeHeight: number;
  minContentHeight: number;
}): number => {
  const viewportBudget = viewportHeight - chromeHeight;
  const anchorBudget = availableSpaceFromAnchor - chromeHeight;
  const preferredBudget = Math.max(minContentHeight, anchorBudget);
  return Math.max(0, Math.floor(Math.min(viewportBudget, preferredBudget)));
};

/** Keep the preferred placement on ties, and use the most room when no candidate fully fits. */
export const findBestPopoverPosition = (
  positions: readonly [EPosition, ...EPosition[]],
  measureOverflow: (position: EPosition) => number,
): EPosition => {
  let bestPosition = positions[0];
  let leastOverflow = Infinity;
  for (const candidate of positions) {
    const overflow = measureOverflow(candidate);
    if (overflow < leastOverflow) {
      bestPosition = candidate;
      leastOverflow = overflow;
    }
    if (overflow === 0) break;
  }
  return bestPosition;
};

/** Keep an open popover attached as its content, anchor or viewport changes. */
export const observePopoverPosition = (
  popover: HTMLElement,
  anchor: HTMLElement | null,
  update: (reselectPosition: boolean) => void,
): (() => void) => {
  const subscriptions = new SubscriptionManager();
  let frame: number | undefined;
  let reselectPosition = false;
  let stopped = false;

  const schedule = (reselect: boolean): void => {
    if (stopped) return;
    reselectPosition ||= reselect;
    if (frame !== undefined) return;

    // Batch layout writes outside ResizeObserver delivery to avoid resize feedback loops.
    frame = requestAnimationFrame(() => {
      frame = undefined;
      const shouldReselect = reselectPosition;
      reselectPosition = false;
      update(shouldReselect);
    });
  };

  subscriptions.subscribeToElementResize(popover, () => schedule(false));
  subscriptions.subscribeToResize(() => schedule(true));
  if (anchor) {
    subscriptions.subscribeToElementResize(anchor, () => schedule(false));
    subscriptions.subscribeToAncestorScrolls(anchor, () => schedule(true));
  }

  return () => {
    stopped = true;
    subscriptions.disposeSubscriptions();
    if (frame !== undefined) cancelAnimationFrame(frame);
  };
};
